import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, message, notification } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { saveNow } from '../../collab/flushPendingSaves';
import DuotoneLightIcon from './DuotoneLightIcon';

const FAILURE_NOTICE_KEY = 'autosave-failure';
// En bas à gauche, sur les panneaux latéraux : en haut à droite elle
// recouvrirait l'indicateur et son bouton « Enregistrer », en bas à droite les
// boutons des fenêtres (alignés à droite).
const NOTICE_PLACEMENT = 'bottomLeft';

// Notification ouverte au premier échec d'enregistrement : ce qui se passe, ce
// que l'utilisateur doit faire, et un bouton pour enregistrer sans attendre.
// Remplacée par une confirmation quand un envoi réussi a tout enregistré
// (autosaveRecoveries). Si la file est abandonnée sans avoir été enregistrée
// (changement de document, restauration), elle se ferme sans confirmation.
const useSaveFailureNotice = (status, onSave) => {
  const { t } = useTranslation();
  const recoveries = useStore((state) => state.autosaveRecoveries);
  const previous = useRef({ status: false, recoveries });

  useEffect(() => {
    const prev = previous.current;
    previous.current = { status, recoveries };
    if (recoveries !== prev.recoveries) {
      notification.success({
        key: FAILURE_NOTICE_KEY,
        placement: NOTICE_PLACEMENT,
        message: t('save-recovered-title'),
        description: t('save-recovered-description'),
        duration: 5,
      });
    } else if (!status && prev.status) {
      notification.destroy(FAILURE_NOTICE_KEY);
    }
    if (status && status !== prev.status) {
      notification.warning({
        key: FAILURE_NOTICE_KEY,
        placement: NOTICE_PLACEMENT,
        message: t('save-failure-title'),
        description: t(
          status === 'retrying'
            ? 'save-failure-pending-description'
            : 'save-failure-rejected-description'
        ),
        duration: 0,
        actions: (
          <Button type="primary" size="small" onClick={onSave}>
            {t('save-now-long')}
          </Button>
        ),
      });
    }
  }, [status, recoveries, onSave, t]);
};

// Indicateur de statut d'enregistrement. Reflète l'autosave granulaire :
// « Enregistrement… » tant qu'une requête de persistance est en vol,
// « Modifications non enregistrées » tant que des modifications attendent un
// nouvel essai ou la reconnexion, « Enregistrement refusé » après un refus du
// serveur, « Enregistré » sinon. Dans les deux cas d'échec, un bouton
// « Enregistrer » permet de relancer l'envoi ; il disparaît dès qu'un
// enregistrement réussit, manuel ou automatique.
const SaveIndicator = ({ style }) => {
  const { t } = useTranslation();
  const isAutosaving = useStore((state) => state.isAutosaving);
  const autosaveFailed = useStore((state) => state.autosaveFailed);
  const [isSavingNow, setIsSavingNow] = useState(false);

  const handleSaveNow = useCallback(async () => {
    setIsSavingNow(true);
    const saved = await saveNow();
    setIsSavingNow(false);
    if (!saved) {
      // Refus du serveur : pas de nouvel essai automatique à promettre.
      const stillRejected = useStore.getState().autosaveFailed === 'rejected';
      message.warning({
        content: t(stillRejected ? 'save-now-still-rejected' : 'save-now-still-failing'),
        key: 'save-now',
      });
    }
  }, [t]);

  useSaveFailureNotice(autosaveFailed, handleSaveNow);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: 'white',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {isAutosaving ? (
        <>
          <FontAwesomeIcon
            icon={icon({ name: 'arrows-repeat', style: 'light' })}
          />
          <span>{t('saving')}</span>
        </>
      ) : autosaveFailed ? (
        <span
          title={t(autosaveFailed === 'retrying' ? 'save-pending-hint' : 'save-failed-hint')}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={icon({ name: 'triangle-exclamation', style: 'solid' })} style={{ color: '#ffb020' }} />
          <span>{t(autosaveFailed === 'retrying' ? 'save-pending' : 'save-failed')}</span>
        </span>
      ) : (
        <>
          <span style={{ position: 'relative', width: '1em', fontSize: 14 }}>
            <DuotoneLightIcon name="cloud" weight="regular" secondaryOpacity={0.1} />
            <FontAwesomeIcon
              icon={icon({ name: 'check', style: 'solid' })}
              style={{
                color: 'rgb(41, 123, 73)',
                fontSize: 10,
                position: 'absolute',
                top: '58%',
                left: '58%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          </span>
          <span>{t('saved')}</span>
        </>
      )}
      {autosaveFailed && (
        <Button size="small" loading={isSavingNow} onClick={handleSaveNow}>
          {t('save-now')}
        </Button>
      )}
    </div>
  );
};

export default SaveIndicator;
